import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ADJUSTMENT_TYPES, FormData } from "./useContractAdjustmentWizard";
import {
  getAllowedDirections,
  getDirectionLabel,
} from "./contractAdjustmentsUtils";

export type ContractAdjustmentDataStepProps = {
  formData: FormData;
  errors: Record<string, string>;
  handleInputChange: (field: keyof FormData, value: string) => void;
  handleAdjustmentTypeChange: (value: string) => void;
};

export function ContractAdjustmentDataStep({
  formData,
  errors,
  handleInputChange,
  handleAdjustmentTypeChange,
}: ContractAdjustmentDataStepProps) {
  const allowedDirections = getAllowedDirections(formData.adjustmentType);
  const isDirectionLocked = allowedDirections.length === 1;

  return (
    <div className="grid gap-4 py-2">
      {/* Title */}
      <div className="grid gap-2">
        <Label htmlFor="title">Título *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={e => handleInputChange("title", e.target.value)}
          placeholder="Ej: Ampliación de tubería adicional"
          className={errors.title ? "border-red-500" : ""}
        />
        {errors.title && <p className="text-sm text-red-500">{errors.title}</p>}
      </div>

      {/* Reason */}
      <div className="grid gap-2">
        <Label htmlFor="reason">Motivo *</Label>
        <Textarea
          id="reason"
          value={formData.reason}
          onChange={e => handleInputChange("reason", e.target.value)}
          placeholder="Describe el motivo de la modificación"
          className={errors.reason ? "border-red-500" : ""}
        />
        {errors.reason && <p className="text-sm text-red-500">{errors.reason}</p>}
      </div>

      {/* Adjustment type */}
      <div className="grid gap-2">
        <Label htmlFor="adjustmentType">Tipo de modificación *</Label>
        <Select
          value={formData.adjustmentType}
          onValueChange={handleAdjustmentTypeChange}
        >
          <SelectTrigger id="adjustmentType">
            <SelectValue placeholder="Selecciona tipo" />
          </SelectTrigger>
          <SelectContent>
            {ADJUSTMENT_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isDirectionLocked && (
          <p className="text-xs text-muted-foreground">
            {formData.adjustmentType === "contract_extension" && "Las ampliaciones de contrato solo pueden incrementar el importe."}
            {formData.adjustmentType === "additional_work" && "Los trabajos adicionales solo pueden incrementar el importe."}
            {formData.adjustmentType === "contract_reduction" && "Las reducciones de alcance solo pueden disminuir el importe."}
          </p>
        )}
      </div>

      {/* Direction — hide when locked */}
      {!isDirectionLocked && (
        <div className="grid gap-2">
          <Label>Dirección *</Label>
          <RadioGroup
            value={formData.direction}
            onValueChange={value => handleInputChange("direction", value)}
            className="flex gap-4"
          >
            {allowedDirections.map(dir => (
              <div key={dir} className="flex items-center gap-2">
                <RadioGroupItem value={dir} id={`direction-${dir}`} />
                <Label htmlFor={`direction-${dir}`} className="cursor-pointer">{getDirectionLabel(dir)}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      )}

      {/* Amount */}
      <div className="grid gap-2">
        <Label htmlFor="amount">Importe (€) *</Label>
        <Input
          id="amount"
          type="text"
          inputMode="decimal"
          value={formData.amount}
          onChange={e => {
            const raw = e.target.value.replace(/[^0-9.,]/g, "");
            handleInputChange("amount", raw);
          }}
          placeholder="Ej: 15000"
          className={errors.amount ? "border-red-500" : ""}
        />
        {errors.amount && <p className="text-sm text-red-500">{errors.amount}</p>}
      </div>

      {/* Effective date */}
      <div className="grid gap-2">
        <Label htmlFor="effectiveDate">Fecha efectiva *</Label>
        <Input
          id="effectiveDate"
          type="date"
          value={formData.effectiveDate}
          onChange={e => handleInputChange("effectiveDate", e.target.value)}
          className={errors.effectiveDate ? "border-red-500" : ""}
        />
        {errors.effectiveDate && <p className="text-sm text-red-500">{errors.effectiveDate}</p>}
      </div>

      {/* Reference */}
      <div className="grid gap-2">
        <Label htmlFor="reference">Referencia</Label>
        <Input
          id="reference"
          value={formData.reference}
          onChange={e => handleInputChange("reference", e.target.value)}
          placeholder="Ej: Nº presupuesto o pedido"
          maxLength={120}
        />
      </div>
    </div>
  );
}